import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  effect,
  inject,
  input,
  output,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'

import {
  FlowMappings,
  ProcessData,
  ProcessDataModel,
  RuntimeComponentDefinition,
  TemplateConfig,
  addAsyncValidatorsToRegistry,
  addValidatorsToRegistry,
  getReusableTestDataStore,
  initReusableTest,
  resetStores,
  screenTemplateConfig$,
  setAccessToken,
  updateReusableTestDataStore,
} from '@flowx/core-sdk'

import { FlxTreeNodeComponent } from '../renderer/tree-node/tree-node.component'
import {
  FLX_ASYNC_VALIDATORS,
  FLX_VALIDATORS,
  NgAsyncValidator,
  NgSyncValidator,
} from '../utils/validators'

@Component({
  selector: 'flx-template-renderer',
  imports: [FlxTreeNodeComponent],
  template: ` @if (templateConfig$(); as templateConfig) {
    <flx-tree-node [templateConfig]="templateConfig.rootTemplates" />
  }`,
  styles: ``,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateRendererComponent implements OnInit, OnDestroy {
  #destroyRef = inject(DestroyRef)
  apiUrl = input.required<string>()
  authToken = input.required<string>()
  processApiPath = input.required<string>()
  staticAssetsPath = input.required<string>()

  projectId = input.required<string>()
  projectVersionId = input.required<string>()
  workspaceId = input.required<string>()
  organizationId = input.required<string>()
  dataModel = input.required<ProcessDataModel>()

  themeId = input.required<string>()
  locale = input.required<string>()
  language = input.required<string>()

  reusableStartData = input.required<ProcessData>()
  templateConfig = input.required<TemplateConfig>()
  customComponents = input<RuntimeComponentDefinition[]>([])
  ccMappings = input<FlowMappings>({})

  dataStoreChanged = output<ProcessData>()

  templateConfig$ = toSignal(screenTemplateConfig$)

  constructor() {
    effect(() => {
      const authToken = this.authToken()
      if (!authToken) {
        return
      }
      // Updates the global config and re-authenticates live SSE connections.
      setAccessToken(authToken)
    })
  }

  ngOnInit(): void {
    // register built-in validators
    addValidatorsToRegistry<NgSyncValidator>(FLX_VALIDATORS)
    addAsyncValidatorsToRegistry<NgAsyncValidator>(FLX_ASYNC_VALIDATORS)
    initReusableTest({
      apiUrl: this.apiUrl(),
      authToken: this.authToken(),
      processApiPath: this.processApiPath(),
      staticAssetsPath: this.staticAssetsPath(),
      appInfo: {
        appId: this.projectId(),
        appVersionId: this.projectVersionId(),
      },
      themeId: this.themeId(),
      language: this.language(),
      locale: this.locale(),
      reusableStartData: this.reusableStartData(),
      templateConfig: this.templateConfig(),
      workspaceId: this.workspaceId(),
      organizationId: this.organizationId(),
      dataModel: this.dataModel(),
      customComponents: this.customComponents(),
      ccMappings: this.ccMappings(),
    })

    getReusableTestDataStore()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((data) => {
        this.dataStoreChanged.emit(data)
      })
  }

  updateReusableDataStore(data: ProcessData): void {
    updateReusableTestDataStore(data)
  }

  ngOnDestroy(): void {
    resetStores()
  }
}
